import { TimeMap, TimeAllocationData, monthToNumber } from './types';

export function initializeTimeMap(customers: any[], years: string[]): Map<string, TimeMap> {
  const timeMap = new Map<string, TimeMap>();
  
  customers.forEach(customer => {
    timeMap.set(customer.name, {
      color: customer.color,
      years: Object.fromEntries(years.map(year => [year, 0]))
    });
  });

  return timeMap;
}

export function calculateCustomerDurations(projects: any[]): Map<string, number> {
  const customerTotalDuration = new Map<string, number>();
  
  projects.forEach(project => {
    if (!project.customer || !project.year || !project.month || !project.duration) return;

    const customerName = project.customer.name;
    const durationMonths = parseInt(project.duration.name.match(/\d+/)[0]);
    
    customerTotalDuration.set(
      customerName, 
      (customerTotalDuration.get(customerName) || 0) + durationMonths
    );
  });

  return customerTotalDuration;
}

export function processTimeAllocation(
  projects: any[],
  timeMap: Map<string, TimeMap>,
  years: string[]
): void {
  projects.forEach(project => {
    if (!project.customer || !project.year || !project.month || !project.duration) return;

    const customerName = project.customer.name;
    const startYear = parseInt(project.year);
    const startMonth = monthToNumber[project.month];
    const durationMonths = parseInt(project.duration.name.match(/\d+/)[0]);

    let remainingMonths = durationMonths;
    let currentYear = startYear;
    let currentMonth = startMonth;

    while (remainingMonths > 0) {
      const yearStr = currentYear.toString();
      if (years.includes(yearStr)) {
        const monthsInYear = Math.min(remainingMonths, 12 - currentMonth);
        const customer = timeMap.get(customerName)!;
        customer.years[yearStr] = (customer.years[yearStr] || 0) + monthsInYear;
      }
      remainingMonths -= (12 - currentMonth);
      currentYear++;
      currentMonth = 0;
    }
  });
}

export function formatTimeAllocationData(
  timeMap: Map<string, TimeMap>,
  years: string[],
  activeCustomers: string[],
  chartColors: string[]
): TimeAllocationData {
  const timeData = years.map(year => {
    const yearData: { [key: string]: number } = {};
    let totalMonths = 0;

    // First pass: collect months for active customers
    timeMap.forEach((customer, customerName) => {
      if (activeCustomers.includes(customerName)) {
        const months = customer.years[year] || 0;
        if (months > 0) { // Only include customers with projects in this year
          yearData[customerName] = months;
          totalMonths += months;
        }
      }
    });

    // Second pass: convert to percentages, only for customers with activity
    const yearPercentages = {
      year,
      ...Object.fromEntries(
        Object.entries(yearData)
          .filter(([_, months]) => months > 0) // Extra safety check
          .map(([customer, months]) => [
            customer,
            totalMonths > 0 ? months / totalMonths : 0
          ])
      )
    };

    return yearPercentages;
  });

  // Get the list of customers that have any activity in any year
  const activeCustomersWithData = Array.from(new Set(
    timeData.flatMap(yearData => 
      Object.keys(yearData).filter(key => key !== 'year')
    )
  ));

  return {
    data: timeData,
    customers: activeCustomersWithData,
    colors: Object.fromEntries(
      activeCustomersWithData.map((name, i) => [
        name,
        timeMap.get(name)?.color || chartColors[i % chartColors.length]
      ])
    )
  };
}